import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCompanyAgreements, useCurrentAgreement, useDeactivateAgreement } from '@/lib/adminApi';
import type { AgreementSummary } from '@ventin/shared';
import { AgreementFormDialog } from './AgreementFormDialog';

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatUsd(rate: string): string {
  const n = Number(rate);
  if (!Number.isFinite(n)) return rate;
  return USD.format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

/**
 * CompanyAgreementsPanel — the company-scoped agreement UI (AGR-01..04):
 * active Agreement card, read-only history table, and AgreementFormDialog.
 * Extracted from AgreementsPage so it can render inside the company profile
 * (CompanyDetailPage); the legacy /admin/agreements page reuses it unchanged.
 */
export function CompanyAgreementsPanel({ companyId }: { companyId: string }) {
  const { data: currentData } = useCurrentAgreement(companyId);
  const { data: historyData } = useCompanyAgreements(companyId);

  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<AgreementSummary | undefined>(undefined);

  const deactivateMut = useDeactivateAgreement();

  const active: AgreementSummary | null = currentData?.agreement ?? null;
  const history: AgreementSummary[] = useMemo(() => {
    const list = historyData?.agreements ?? [];
    return list.filter((a) => !a.isActive).sort((a, b) => b.startDate.localeCompare(a.startDate));
  }, [historyData]);

  async function handleDeactivate() {
    if (!active) return;
    try {
      await deactivateMut.mutateAsync({ id: active.id });
      toast.success('Saved');
    } catch {
      toast.error("Couldn't save — try again");
    }
  }

  return (
    <>
      {/* Active Agreement card */}
      <section className="mb-8">
        <h3 className="text-sm font-medium uppercase text-muted-foreground mb-2">
          Current Agreement
        </h3>
        {active ? (
          <div className="rounded-md border bg-background p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <div className="text-2xl font-semibold">{formatUsd(active.customRate)}</div>
                <div className="text-sm text-muted-foreground">
                  {active.includedIncrements} included increments · cycle day {active.cycleDay}
                </div>
                <div className="text-sm text-muted-foreground">
                  Start {formatDate(active.startDate)} · GA4:{' '}
                  {active.gaPropertyId ?? '(Company default)'}
                </div>
                {active.legacyPlanRef && (
                  <div className="text-xs text-muted-foreground">
                    Legacy ref: {active.legacyPlanRef}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditing(active);
                    setFormMode('edit');
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleDeactivate}
                  disabled={deactivateMut.isPending}
                >
                  Deactivate
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditing(undefined);
                    setFormMode('create');
                  }}
                >
                  New Agreement
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <Alert>
            <AlertDescription className="flex items-center justify-between gap-4">
              <span>No active Agreement for this company.</span>
              <Button
                size="sm"
                onClick={() => {
                  setEditing(undefined);
                  setFormMode('create');
                }}
              >
                New Agreement
              </Button>
            </AlertDescription>
          </Alert>
        )}
      </section>

      {/* History table */}
      <section>
        <h3 className="text-sm font-medium uppercase text-muted-foreground mb-2">History</h3>
        <div className="rounded-md border bg-background overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Start</th>
                <th className="px-4 py-3 font-medium">End</th>
                <th className="px-4 py-3 font-medium">Rate</th>
                <th className="px-4 py-3 font-medium">Included</th>
                <th className="px-4 py-3 font-medium">Cycle day</th>
                <th className="px-4 py-3 font-medium">GA4</th>
                <th className="px-4 py-3 font-medium">Legacy ref</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    No prior Agreements.
                  </td>
                </tr>
              )}
              {history.map((a) => (
                <tr key={a.id} className="border-t">
                  <td className="px-4 py-3 text-muted-foreground">{formatDate(a.startDate)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{formatDate(a.endDate)}</td>
                  <td className="px-4 py-3">{formatUsd(a.customRate)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.includedIncrements}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.cycleDay}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.gaPropertyId ?? '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.legacyPlanRef ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <AgreementFormDialog
        mode={formMode ?? 'create'}
        companyId={companyId}
        initial={editing}
        open={formMode !== null}
        onOpenChange={(o) => {
          if (!o) setFormMode(null);
        }}
      />
    </>
  );
}
